import type { ReactNode } from 'react';
import { AlertTriangle, Info, TrendingDown, TrendingUp, Minus, Ban } from 'lucide-react';
import { cn } from '@/utils/cn';
import type {
  ExceptionItem, ExceptionSeverity, HealthStatus, KpiCard, KpiFormat,
} from '@/types/analytics.types';

/**
 * Shared presentation kit for the management-analytics screens.
 * Keeps eight report pages visually consistent and stops each one reinventing number formatting.
 */

// ── Shared Tailwind class strings (match the existing report pages) ────────────

export const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
export const LABEL_CLS =
  'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
export const TH_CLS =
  'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
export const TH_R_CLS =
  'px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
export const AMT_CLS = 'px-3 py-2 text-sm text-right tabular-nums';
export const CARD_CLS =
  'bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700';

// ── Date helpers ──────────────────────────────────────────────────────────────

export function today(): string {
  return new Date().toISOString().split('T')[0];
}

export function firstOfMonth(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}

/** Start of the month `months` back from today — the usual default for a trend window. */
export function monthsAgo(months: number): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() - months, 1).toISOString().split('T')[0];
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── Number formatting ─────────────────────────────────────────────────────────

export function fmtAmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Like fmtAmt but renders a real zero as "0.00" rather than an em dash. */
export function fmtAmtZero(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${n.toFixed(2)}%`;
}

export function fmtRatio(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(2);
}

export function fmtByFormat(value: number | null | undefined, format: KpiFormat): string {
  if (value === null || value === undefined) return '—';
  switch (format) {
    case 'PERCENT':  return fmtPct(value);
    case 'RATIO':    return fmtRatio(value);
    case 'COUNT':    return value.toLocaleString('en-US');
    case 'DAYS':     return `${value.toFixed(1)} days`;
    case 'CURRENCY':
    default:         return fmtAmtZero(value);
  }
}

// ── Status ────────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<HealthStatus, string> = {
  HEALTHY:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  WATCH:    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  CRITICAL: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  NEUTRAL:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
  NO_DATA:  'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
};

const STATUS_LABELS: Record<HealthStatus, string> = {
  HEALTHY:  'Healthy',
  WATCH:    'Watch',
  CRITICAL: 'Critical',
  NEUTRAL:  'Level',
  NO_DATA:  'No data',
};

export const StatusPill = ({ status }: { status: HealthStatus }) => (
  <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap', STATUS_STYLES[status])}>
    {STATUS_LABELS[status]}
  </span>
);

/** Traffic-light dot — for dense tables where a pill would be too heavy. */
export const StatusDot = ({ status }: { status: HealthStatus }) => {
  const colour: Record<HealthStatus, string> = {
    HEALTHY:  'bg-green-500',
    WATCH:    'bg-amber-500',
    CRITICAL: 'bg-red-500',
    NEUTRAL:  'bg-blue-400',
    NO_DATA:  'bg-gray-300 dark:bg-gray-600',
  };
  return (
    <span
      className={cn('inline-block w-2.5 h-2.5 rounded-full', colour[status])}
      title={STATUS_LABELS[status]}
    />
  );
};

// ── Change indicator ──────────────────────────────────────────────────────────

interface DeltaProps {
  percent: number | null | undefined;
  /** For costs, a rise is bad. For revenue and profit, a rise is good. */
  higherIsBetter?: boolean;
}

export const Delta = ({ percent, higherIsBetter = true }: DeltaProps) => {
  if (percent === null || percent === undefined) {
    return <span className="text-xs text-gray-400 dark:text-gray-600">—</span>;
  }

  const flat = Math.abs(percent) < 0.005;
  const good = higherIsBetter ? percent > 0 : percent < 0;

  const Icon = flat ? Minus : percent > 0 ? TrendingUp : TrendingDown;
  const tone = flat
    ? 'text-gray-500 dark:text-gray-400'
    : good
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium tabular-nums', tone)}>
      <Icon size={12} />
      {percent > 0 ? '+' : ''}{percent.toFixed(1)}%
    </span>
  );
};

// ── KPI card ──────────────────────────────────────────────────────────────────

/** KPIs where a decrease is the good outcome. */
const LOWER_IS_BETTER = new Set([
  'COST_OF_SALES', 'OPERATING_EXPENSES', 'ACCOUNTS_PAYABLE', 'CASH_BURN_RATE',
]);

export const KpiTile = ({ kpi }: { kpi: KpiCard }) => {
  const unavailable = kpi.value === null || kpi.status === 'NO_DATA';

  return (
    <div className={cn(CARD_CLS, 'p-3 flex flex-col gap-1 min-w-0')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate" title={kpi.label}>
          {kpi.label}
        </span>
        {unavailable && <Ban size={12} className="text-gray-400 shrink-0" />}
      </div>

      <div
        className={cn(
          'text-lg font-bold tabular-nums truncate',
          unavailable            ? 'text-gray-400 dark:text-gray-600'
          : kpi.status === 'CRITICAL' ? 'text-red-600 dark:text-red-400'
          : kpi.status === 'WATCH'    ? 'text-amber-600 dark:text-amber-400'
          :                             'text-gray-900 dark:text-white',
        )}
      >
        {fmtByFormat(kpi.value, kpi.format)}
      </div>

      {!unavailable && kpi.changePercent !== null && (
        <Delta percent={kpi.changePercent} higherIsBetter={!LOWER_IS_BETTER.has(kpi.key)} />
      )}

      {kpi.note && (
        <span className="text-[10px] leading-tight text-gray-400 dark:text-gray-500">{kpi.note}</span>
      )}
    </div>
  );
};

// ── Unavailable metrics ───────────────────────────────────────────────────────

/**
 * Renders the metrics the backend declined to compute. Showing them explicitly is the point:
 * a blank or zero KPI reads as "we measured this and it is nothing", which would be a lie.
 */
export const UnavailableMetrics = ({ metrics }: { metrics: string[] }) => {
  if (metrics.length === 0) return null;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Info size={14} className="text-gray-400" />
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
          Not shown on this report
        </span>
      </div>
      <ul className="space-y-0.5 pl-6 list-disc">
        {metrics.map(m => (
          <li key={m} className="text-xs text-gray-500 dark:text-gray-400">{m}</li>
        ))}
      </ul>
    </div>
  );
};

// ── Exception panel ───────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<ExceptionSeverity, string> = {
  CRITICAL: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
  WARNING:  'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  INFO:     'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50',
};

const SEVERITY_ICON_TONE: Record<ExceptionSeverity, string> = {
  CRITICAL: 'text-red-500',
  WARNING:  'text-amber-500',
  INFO:     'text-gray-400',
};

export const ExceptionPanel = ({ items }: { items: ExceptionItem[] }) => {
  if (items.length === 0) {
    return (
      <div className={cn(CARD_CLS, 'p-4 text-sm text-gray-500 dark:text-gray-400')}>
        No exceptions detected for this period.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((e, i) => (
        <div
          key={`${e.category}-${i}`}
          className={cn('border rounded-lg p-3 flex gap-3', SEVERITY_STYLES[e.severity])}
        >
          <AlertTriangle size={16} className={cn('shrink-0 mt-0.5', SEVERITY_ICON_TONE[e.severity])} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{e.title}</span>
              {e.changePercent !== null && <Delta percent={e.changePercent} />}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{e.detail}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Section wrapper ───────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export const Section = ({ title, subtitle, actions, children, className }: SectionProps) => (
  <div className={cn(CARD_CLS, 'flex flex-col min-h-0', className)}>
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
        {subtitle && (
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        )}
      </div>
      {actions}
    </div>
    <div className="p-4 min-h-0">{children}</div>
  </div>
);

// ── Empty state ───────────────────────────────────────────────────────────────

export const EmptyState = ({ message }: { message: string }) => (
  <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm py-10">
    {message}
  </div>
);

// ── Sparkline ─────────────────────────────────────────────────────────────────

/**
 * Inline SVG sparkline — cheap enough to render once per table row.
 * `higherIsBetter` decides the colour: a rising expense line is bad, a rising profit line is good.
 */
export const Sparkline = ({
  values,
  width = 80,
  height = 20,
  higherIsBetter = false,
}: { values: number[]; width?: number; height?: number; higherIsBetter?: boolean }) => {
  if (values.length < 2) return <span className="text-xs text-gray-400">—</span>;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / span) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const rising = values[values.length - 1] >= values[0];
  const good   = higherIsBetter ? rising : !rising;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.5}
        className={good ? 'stroke-green-500' : 'stroke-red-500'}
      />
    </svg>
  );
};

/** Recharts palette — one place so every chart in the suite agrees. */
export const CHART_COLOURS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
];
